import { useState } from 'react';
import { DeleteButton, Empty, ErrorBanner, Field, Loading, Modal } from '../../components/ui';
import { api } from '../../lib/api';
import { blankCostLine, bucketTotals } from '../../lib/amCosts';
import { formatDate, formatNumber } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import type { AmCostLine, AmRecommendation, AmRecommendationTemplate, Me } from '../../lib/types';
import { AmProductPicker, costLinesFromProducts, type PickedProduct } from './AmProductPicker';
import { CostLinesEditor } from './amUi';

/**
 * Recommendation templates — migration 190.
 *
 * A template is saved from the Add recommendation form ("Also save as a
 * template") and picked from the top of it next time. This tab is the list of
 * them: anyone can read it, and an administrator can edit or delete one —
 * matching requireAdmin on the API's PUT and DELETE, since every account
 * manager starts from the same templates.
 */

export const TEMPLATES_PATH = '/am/recommendation-templates';

/** A template's products in the shape the product picker holds. */
export const templateProducts = (t: AmRecommendationTemplate): PickedProduct[] =>
  t.Products.map((p) => ({
    productId: p.ProductId,
    quantity: p.Quantity,
    name: p.Name,
    unitPrice: p.UnitPrice,
    periodType: p.PeriodType,
    vendor: p.Vendor,
  }));

/** A template's cost lines, never empty — the editor always shows one row. */
export const templateLines = (t: AmRecommendationTemplate): AmCostLine[] =>
  t.CostLines.length ? t.CostLines : [blankCostLine()];

/** What a template's price comes to, in a few words for the list. */
function priceSummary(lines: AmCostLine[]) {
  const t = bucketTotals([{ CostLines: lines } as Partial<AmRecommendation>]);
  const parts = [
    t.oneTime > 0 && `${formatNumber(t.oneTime, 'currency')} one-time`,
    t.monthly > 0 && `${formatNumber(t.monthly, 'currency')}/mo`,
    t.annual > 0 && `${formatNumber(t.annual, 'currency')}/yr`,
  ].filter(Boolean);
  return parts.length ? parts.join(' · ') : '—';
}

export function AmRecTemplates() {
  const list = useResource<AmRecommendationTemplate[]>(TEMPLATES_PATH);
  const me = useResource<Me>('/me');
  const isAdmin = me.data?.appRole === 'admin';
  const [editing, setEditing] = useState<AmRecommendationTemplate | null>(null);
  const [error, setError] = useState<string | null>(null);

  const remove = async (t: AmRecommendationTemplate) => {
    setError(null);
    try {
      await api.del(`${TEMPLATES_PATH}/${t.AmRecommendationTemplateId}`);
      list.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete the template');
    }
  };

  if (list.loading) return <Loading label="Loading the recommendation templates" />;
  if (list.error) return <ErrorBanner message={list.error} />;
  const templates = list.data ?? [];

  return (
    <section className="card">
      <div className="card-head">
        <div className="grow">
          <h3>Recommendation templates</h3>
          <p className="sub">
            Saved from the Add recommendation form, and offered at the top of it.
            {isAdmin ? ' You can edit or delete any of them.' : ' An administrator can edit or delete them.'}
          </p>
        </div>
      </div>
      {error && <ErrorBanner message={error} />}
      {templates.length === 0 ? (
        <div className="card-body">
          <Empty title="No templates yet">
            <p>
              Add a recommendation on any client and tick &ldquo;Also save as a template&rdquo; — it will appear here and in
              the template list on the form.
            </p>
          </Empty>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Template</th>
                <th>Price</th>
                <th className="num">Products</th>
                <th>Saved</th>
                {isAdmin && <th />}
              </tr>
            </thead>
            <tbody>
              {templates.map((t) => (
                <tr key={t.AmRecommendationTemplateId}>
                  <td>
                    <div className="strong">{t.Name}</div>
                    {t.Title !== t.Name && <div className="xs muted">{t.Title}</div>}
                  </td>
                  <td className="xs">{priceSummary(t.CostLines)}</td>
                  <td className="num">{t.Products.length || '—'}</td>
                  <td className="xs">
                    {formatDate(t.UpdatedUtc ?? t.CreatedUtc)}
                    {(t.UpdatedBy ?? t.CreatedBy) && <div className="subtle">{t.UpdatedBy ?? t.CreatedBy}</div>}
                  </td>
                  {isAdmin && (
                    <td className="actions">
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setEditing(t)}>
                        Edit
                      </button>
                      <DeleteButton what={`the template “${t.Name}”`} onConfirm={() => void remove(t)} />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <TemplateEditor
          template={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            list.reload();
          }}
        />
      )}
    </section>
  );
}

/** An administrator's editor for one template: the recommendation form's own fields, less the status. */
function TemplateEditor({
  template,
  onClose,
  onSaved,
}: {
  template: AmRecommendationTemplate;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(template.Name);
  const [title, setTitle] = useState(template.Title);
  const [finding, setFinding] = useState(template.Finding ?? '');
  const [description, setDescription] = useState(template.Description ?? '');
  const [lines, setLines] = useState<AmCostLine[]>(templateLines(template));
  const [products, setProducts] = useState<PickedProduct[]>(templateProducts(template));
  const [effort, setEffort] = useState(template.Effort ?? '');
  const [hours, setHours] = useState(template.Hours ?? 0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await api.put(`${TEMPLATES_PATH}/${template.AmRecommendationTemplateId}`, {
        name: name.trim(),
        title: title.trim(),
        finding,
        description,
        costLines: lines,
        products: products.filter((p) => p.productId > 0).map((p) => ({ productId: p.productId, quantity: p.quantity })),
        effort,
        hours,
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the template');
      setSaving(false);
    }
  }

  return (
    <Modal
      title="Edit template"
      onClose={onClose}
      size="xl"
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={saving || !name.trim() || !title.trim()}
            onClick={() => void save()}
          >
            {saving ? 'Saving…' : 'Save template'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <Field label="Template name" hint="What it is called in the list on the Add recommendation form.">
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Title">
        <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <Field label="Finding">
        <textarea className="textarea" rows={2} value={finding} onChange={(e) => setFinding(e.target.value)} />
      </Field>
      <Field label="Description and scope">
        <textarea className="textarea" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
      </Field>
      <AmProductPicker
        items={products}
        onChange={setProducts}
        onApplyAsCost={(items) => setLines(costLinesFromProducts(items, lines[0]?.priority ?? 'Medium'))}
      />
      <CostLinesEditor lines={lines} onChange={setLines} />
      <div className="form-grid">
        <Field label="Effort or timeline">
          <input className="input" value={effort} onChange={(e) => setEffort(e.target.value)} />
        </Field>
        <Field label="Estimated hours">
          <input
            className="input"
            type="number"
            min="0"
            step="0.25"
            value={hours}
            onChange={(e) => setHours(Number(e.target.value) || 0)}
          />
        </Field>
      </div>
    </Modal>
  );
}
